import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogActions, DialogContent, Divider, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomButton from "../Common/CustomButton";
import { TagsInputUniqueKeys } from "../Common/TagInputsUniqueKeys";
import useFetch from "../Common/useFetch";
import { AllowedFilterKeysClosingBalance } from "./ClosingBalanceUpload";

export default function ClosingBalanceUploadFilter({
  open,
  onClose,
  appliedFilters,
  setAppliedFilters,
}: {
  open: boolean;
  onClose: () => void;
  appliedFilters: Record<AllowedFilterKeysClosingBalance, string[]>;
  setAppliedFilters: React.Dispatch<React.SetStateAction<Record<AllowedFilterKeysClosingBalance, string[]>>>;
}) {
  const [vendorNames, setVendorNames] = useState<string[]>(appliedFilters.vendorName || []);
  const [PANNumbers, setPANNumbers] = useState<string[]>(appliedFilters.panNumber || []);

  const [vendorNameOptions, setVendorNamesOptions] = useState<{ vendorName: string }[]>([]);
  const [PANOptions, setPANOptions] = useState<{ panNumber: string }[]>([]);

  const getFilterOptions = async () => {
    await useFetch("/closing-balance/filter/options", "GET", {
      config: {
        params: {
          anchorId: 2,
        },
      },
      failureMessage: "Failed to fetch filter options",
      thenCallBack: (response) => {
        const data = response.data.result;
        setVendorNamesOptions(
          data.vendorName.map((name: string) => {
            return { vendorName: name };
          }) || []
        );
        setPANOptions(data.panNumber.map((pan: string) => ({ panNumber: pan.trim() })) || []);
      },
    });
  };

  useEffect(() => {
    getFilterOptions();
  }, []);

  const handleApplyFilter = () => {
    setAppliedFilters({
      vendorName: vendorNames.filter((v) => v.trim() !== ""),
      panNumber: PANNumbers.filter((v) => v.trim() !== ""),
    });
    onClose();
  };

  const handleClearFilters = () => {
    setAppliedFilters({
      vendorName: [],
      panNumber: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", minWidth: "600px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Apply Filter
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Name</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={vendorNames}
                setTags={setVendorNames}
                fullWidth={true}
                label=""
                placeholder="Newline separated Vendor Names"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={vendorNameOptions}
                accessor={"vendorName"}
                uniqueKeyAccessor={"vendorName"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">PAN</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={PANNumbers}
                setTags={setPANNumbers}
                fullWidth={true}
                label=""
                placeholder="Newline separated PAN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={PANOptions}
                accessor={"panNumber"}
                uniqueKeyAccessor={"panNumber"}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <CustomButton
          onClick={() => {
            handleClearFilters();
          }}
          variant="text"
        >
          Clear Filter
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={() => {
            handleApplyFilter();
          }}
        >
          Apply
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
